/**
 * Attempt timer derived purely from simulation time.
 *
 * A timestamp is (tick index + fraction within tick) * dt, where the fraction
 * comes from the swept path, so a finish crossed mid-tick is timed precisely
 * and independently of the rendering frame rate. Elapsed time is computed from
 * the tick difference, so the same run gives the same time bit for bit
 * whenever it starts (absolute times would round differently).
 */
interface Stamp {
  tick: number;
  fraction: number;
}

export class TimerSystem {
  private startStamp: Stamp | null = null;
  private stopStamp: Stamp | null = null;

  constructor(private readonly dt: number) {}

  /** Simulation time in seconds of a point `fraction` through tick `tickIndex`. */
  timeAt(tickIndex: number, fraction: number): number {
    return (tickIndex + fraction) * this.dt;
  }

  start(tickIndex: number, fraction: number): void {
    this.startStamp = { tick: tickIndex, fraction };
    this.stopStamp = null;
  }

  stop(tickIndex: number, fraction: number): number | null {
    if (this.startStamp === null || this.stopStamp !== null) return this.elapsed(tickIndex);
    this.stopStamp = { tick: tickIndex, fraction };
    return this.between(this.startStamp, this.stopStamp);
  }

  /** Run time at a point `fraction` through tick `tickIndex` (0 before the start). */
  at(tickIndex: number, fraction: number): number {
    return this.startStamp === null ? 0 : Math.max(0, this.between(this.startStamp, { tick: tickIndex, fraction }));
  }

  reset(): void {
    this.startStamp = null;
    this.stopStamp = null;
  }

  get running(): boolean {
    return this.startStamp !== null && this.stopStamp === null;
  }

  /** Elapsed seconds at the end of tick `tickIndex` (or at stop time), null if never started. */
  elapsed(tickIndex: number): number | null {
    if (this.startStamp === null) return null;
    return Math.max(0, this.between(this.startStamp, this.stopStamp ?? { tick: tickIndex, fraction: 1 }));
  }

  private between(a: Stamp, b: Stamp): number {
    return (b.tick - a.tick + (b.fraction - a.fraction)) * this.dt;
  }
}
